import { useQuery } from "@tanstack/react-query";
import { useT } from "@ec2/i18n/react";
import type { MessageKey } from "@ec2/i18n";
import type { BucksLedgerEntry } from "@ec2/contracts";
import { useTRPC } from "../lib/api";
import { formatClock } from "../lib/time";
import { useSession } from "../state/session";
import { Sheet } from "./Sheet";
import { LoadingLine } from "./common";

const REASON_KEY: Record<BucksLedgerEntry["reason"], MessageKey> = {
  quest_reward: "ec2.bucks.reason.quest_reward",
  contact_reward: "ec2.bucks.reason.contact_reward",
  order_payment: "ec2.bucks.reason.order_payment",
  order_refund: "ec2.bucks.reason.order_refund",
  admin_adjust: "ec2.bucks.reason.admin_adjust",
  group_bonus: "ec2.bucks.reason.group_bonus",
};

/**
 * Bottom sheet behind the tappable balance in the quest-home header
 * (tour step 3: "Tap the number anytime to see every buck you earned").
 * Newest first, server-capped at 100; reason labels localized here.
 */
export function BucksLedgerSheet({
  eventId,
  timezone,
  onClose,
}: {
  eventId: string;
  timezone?: string;
  onClose: () => void;
}) {
  const t = useT();
  const trpc = useTRPC();
  const { locale } = useSession();
  const ledger = useQuery(trpc.participant.ledger.queryOptions({ eventId }));
  const amountFormat = new Intl.NumberFormat(locale, { signDisplay: "always" });

  return (
    <Sheet label={t("ec2.bucks.ledger.title")} onClose={onClose}>
      <h2 className="pb-3 font-display text-[21px] font-[650] text-fg">{t("ec2.bucks.ledger.title")}</h2>
      {ledger.isLoading && <LoadingLine />}
      {ledger.data?.length === 0 && (
        <p className="py-4 text-center text-[13px] font-medium text-fg-muted">
          {t("ec2.bucks.ledger.empty")}
        </p>
      )}
      {ledger.data && ledger.data.length > 0 && (
        <ul data-testid="ec-ledger-list" className="max-h-[55vh] divide-y divide-border overflow-y-auto">
          {ledger.data.map((entry, index) => (
            <li key={index} data-testid="ec-ledger-row" className="flex items-center gap-3 py-2.5">
              <span className="min-w-0 flex-1">
                <span className="block text-base font-medium text-fg">{t(REASON_KEY[entry.reason])}</span>
                <span className="block text-[12.5px] font-medium text-fg-muted">
                  {formatClock(entry.createdAt, locale, timezone)}
                </span>
              </span>
              <span
                className={
                  `font-display text-[17px] font-[650] tabular-nums ` +
                  (entry.amount >= 0 ? "text-bucks" : "text-danger")
                }
              >
                {amountFormat.format(entry.amount)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Sheet>
  );
}
